import { useEffect, useRef, useState } from 'react';
import willyLoupe from '../../levels/willy/obstacles/m.png';
import {
  WILLY_BLUR_FADE_MS,
  WILLY_BLUR_HOLD_MS,
  WILLY_BLUR_LOUPE_MS,
} from './gameplayConstants';
import './LevelWillyBlurOverlay.css';

type WillyBlurPhase =
  | 'loupeIn'
  | 'blurIn'
  | 'hold'
  | 'blurOut'
  | 'loupeOut';

const PHASE_MS: Record<WillyBlurPhase, number> = {
  loupeIn: WILLY_BLUR_LOUPE_MS,
  blurIn: WILLY_BLUR_FADE_MS,
  hold: WILLY_BLUR_HOLD_MS,
  blurOut: WILLY_BLUR_FADE_MS,
  loupeOut: WILLY_BLUR_LOUPE_MS,
};

const NEXT_PHASE: Record<WillyBlurPhase, WillyBlurPhase | null> = {
  loupeIn: 'blurIn',
  blurIn: 'hold',
  hold: 'blurOut',
  blurOut: 'loupeOut',
  loupeOut: null,
};

type LevelWillyBlurOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelWillyBlurOverlay({
  isPaused,
  onComplete,
}: LevelWillyBlurOverlayProps) {
  const [phase, setPhase] = useState<WillyBlurPhase>('loupeIn');
  const [loupeRaised, setLoupeRaised] = useState(false);
  const remainingMsRef = useRef(PHASE_MS.loupeIn);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setLoupeRaised(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'loupeOut') {
      setLoupeRaised(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      remainingMsRef.current -= now - lastNow;
      lastNow = now;
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  const blurVisible = phase === 'blurIn' || phase === 'hold';

  return (
    <div className="level-willy-blur-overlay" aria-hidden>
      <div
        className={
          blurVisible
            ? 'level-willy-blur-overlay__blur level-willy-blur-overlay__blur--visible'
            : 'level-willy-blur-overlay__blur'
        }
        style={{ transitionDuration: `${WILLY_BLUR_FADE_MS}ms` }}
      />
      <img
        className={
          loupeRaised
            ? 'level-willy-blur-overlay__loupe level-willy-blur-overlay__loupe--raised'
            : 'level-willy-blur-overlay__loupe'
        }
        style={{ transitionDuration: `${WILLY_BLUR_LOUPE_MS}ms` }}
        src={willyLoupe}
        alt=""
      />
    </div>
  );
}
